import React from "react";
import { ExternalLink } from "lucide-react";
import { cn } from "../../lib/utils";
import { LivePreviewSkeleton } from "./LivePreviewSkeleton";

type LivePreviewPanelProps = {
  siteUrl?: string | null;
  onBack: () => void;
  refreshKey?: number;
};

const IFRAME_SANDBOX =
  "allow-scripts allow-same-origin allow-forms allow-popups allow-downloads";

const INITIAL_LOAD_TIMEOUT_MS = 15_000;
const MAX_RETRY_ATTEMPTS = 4;

export const LivePreviewPanel: React.FC<LivePreviewPanelProps> = ({
  siteUrl,
  refreshKey = 0,
}) => {
  const [iframeLoaded, setIframeLoaded] = React.useState(false);
  const [retryAttempt, setRetryAttempt] = React.useState(0);
  const retryTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  const clearRetryTimeout = () => {
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }
  };

  // Reset all state when the parent signals a real content change
  React.useEffect(() => {
    clearRetryTimeout();
    setIframeLoaded(false);
    setRetryAttempt(0);
  }, [refreshKey, siteUrl]);

  // Exponential-backoff retry when the iframe stays in a loading state
  React.useEffect(() => {
    if (iframeLoaded || !siteUrl || retryAttempt >= MAX_RETRY_ATTEMPTS) return;

    const delay = INITIAL_LOAD_TIMEOUT_MS * Math.pow(2, retryAttempt);
    retryTimeoutRef.current = setTimeout(() => {
      setRetryAttempt((prev) => prev + 1);
      setIframeLoaded(false);
    }, delay);

    return clearRetryTimeout;
  }, [iframeLoaded, retryAttempt, siteUrl]);

  const handleIframeSettle = () => {
    clearRetryTimeout();
    setIframeLoaded(true);
  };

  const previewUrl = siteUrl || "#";
  // Include retryAttempt in the query param so each retry bypasses the cache
  const iframeSrc = siteUrl
    ? `${siteUrl}${siteUrl.includes("?") ? "&" : "?"}preview=${refreshKey}&r=${retryAttempt}`
    : undefined;

  return (
    <div className="flex flex-col w-full h-full overflow-hidden relative">
      <div className="relative flex flex-col flex-1 min-h-0 w-full overflow-hidden">
        {/* Mock browser chrome */}
        <div className="h-10 bg-muted/50 border-b border-border flex items-center px-4 shrink-0 justify-between">
          {/* Traffic lights (desktop only) */}
          <div className="gap-1.5 hidden lg:flex">
            <div className="w-3 h-3 rounded-full bg-red-400" />
            <div className="w-3 h-3 rounded-full bg-yellow-400" />
            <div className="w-3 h-3 rounded-full bg-emerald-400" />
          </div>

          {/* URL bar */}
          <div className="flex-1 max-w-md mx-auto lg:mx-4 flex items-center justify-center gap-2 bg-background rounded border border-border py-1 px-3 shadow-sm truncate">
            <span className="w-1.5 h-1.5 rounded-full bg-success shrink-0 animate-pulse" />
            <span className="text-[11px] font-mono text-muted-foreground truncate select-all">
              {previewUrl.replace(/^https?:\/\//, "")}
            </span>
          </div>

          {/* External link */}
          <div className="flex gap-2">
            {siteUrl && (
              <a
                href={siteUrl}
                target="_blank"
                rel="noreferrer"
                className="text-muted-foreground hover:text-foreground transition-colors"
                title="Open in new tab"
              >
                <ExternalLink className="w-4 h-4" />
              </a>
            )}
          </div>
        </div>

        <div className="relative flex-1 min-h-0 overflow-hidden bg-card">
          {siteUrl && iframeSrc ? (
            <>
              {!iframeLoaded ? <LivePreviewSkeleton /> : null}
              <iframe
                key={`${refreshKey}-${retryAttempt}`}
                src={iframeSrc}
                title="Live website preview"
                sandbox={IFRAME_SANDBOX}
                className={cn(
                  "w-full h-full border-0",
                  iframeLoaded ? "opacity-100" : "opacity-0",
                )}
                onLoad={handleIframeSettle}
                onError={handleIframeSettle}
              />
            </>
          ) : (
            <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
              No website URL configured yet.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
